import React from 'react';
import { useMutation } from '@common/hooks';
import { Form, Select } from 'antd';
import { Modal } from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  PdfExportFileArgs,
  PdfExportFileResponse,
  WorksInvoicePdfExportMutation,
  WorksReverseInvoicePdfExportMutation,
} from '@pay-bill/graphql';
import { Store } from 'antd/lib/form/interface';

const LABEL_TYPE = {
  invoice: 'Invoice',
  reverse_invoice: 'Reverse invoice',
};

const MUTATIONS = {
  pdf_invoice: WorksInvoicePdfExportMutation,
  pdf_reverse_invoice: WorksReverseInvoicePdfExportMutation,
};

export const SummaryPdfExportModal = ({
  visible,
  onAction,
  id,
  entityType,
}: {
  visible: boolean;
  onAction: () => void;
  id: string;
  entityType: 'invoice' | 'reverse_invoice';
}) => {
  const [form] = Form.useForm();
  const mutationKey: keyof typeof MUTATIONS = `pdf_${entityType}`;

  const EXPORT_TYPE_OPTIONS =
    entityType === 'invoice'
      ? [
          { value: 'cost_center', label: 'Cost Center' },
          { value: 'role', label: 'Role' },
          { value: 'worker', label: 'Worker' },
          { value: 'week_ending', label: 'Week Ending' },
          { value: 'supplier', label: 'Supplier' },
        ]
      : [{ value: 'hospital', label: 'Hospital' }];

  const [performExport, { loading }] = useMutation<
    PdfExportFileResponse,
    PdfExportFileArgs
  >(MUTATIONS[mutationKey], {
    onCompleted: (response) => {
      const { downloadUrl } = response.result.file;
      if (downloadUrl) {
        window.location.href = downloadUrl;
      }

      createMessage('success', `${LABEL_TYPE[entityType]} downloaded.`);
      onAction();
    },
    onError: () => {
      createMessage('error', 'Download failed. Please try again.');
    },
  });

  const exportFile = (values: Store) => {
    performExport({
      variables: {
        id: id,
        type: values.exportType,
      },
    });
  };

  return (
    <Modal
      visible={visible}
      cancelText="Cancel"
      okText="Download"
      onCancel={onAction}
      title="Summary PDF"
      confirmLoading={loading}
      onOk={form.submit}
      afterClose={() => form.resetFields()}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={exportFile}
        initialValues={
          entityType === 'reverse_invoice'
            ? { exportType: 'hospital' }
            : { exportType: undefined }
        }
      >
        <Form.Item
          name="exportType"
          label="Export Type"
          rules={[
            {
              required: entityType === 'invoice',
              message: 'Please select the export type',
            },
          ]}
        >
          <Select
            disabled={entityType === 'reverse_invoice'}
            placeholder="Select one"
            size="large"
            options={EXPORT_TYPE_OPTIONS}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
